/**
 * SectionsList — Sections + lignes du devis avec drag & drop complet (Phase 3c).
 *
 * Fonctionnalités :
 * - Sections réordonnables via drag handle sur le bandeau.
 * - Lignes réordonnables dans une section ET déplaçables entre sections (dnd-kit).
 * - Chaque section = zone droppable ; drop sur bandeau vide = append.
 * - Sous-total HTVA dans le bandeau (couleur accent_color).
 * - Bouton "+ Ajouter une section" au-dessus (piloté par le parent via onAddSection).
 * - Catalog picker par section + dropdown "Ajouter une ligne".
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, FolderPlus, GripVertical, Package, Plus, StickyNote, BookOpen } from "lucide-react";
import { useState } from "react";
import SectionEditor from "@/components/quote/SectionEditor";
import LineRow from "@/components/quote/LineRow";
import CatalogPicker from "@/components/quote/CatalogPicker";
import CatalogMultiPickerModal from "@/components/quote/CatalogMultiPickerModal";

// -------- Wrappers sortable ------------------------------------------------
function SortableSectionBlock({ section, sectionLines, sIdx, sectionsOrder, accentColor, isDraft, onRenameSection, onRemoveSection, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } =
    useSortable({ id: `section:${section}`, data: { type: "section", section } });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const dragHandle = isDraft ? (
    <button
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      className="mr-1 cursor-grab rounded p-0.5 text-white/80 hover:bg-black/20"
      title="Déplacer la section"
      data-testid={`section-drag-${section}`}
      aria-label="Déplacer la section"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  ) : null;
  return (
    <div ref={setNodeRef} style={style}>
      <SectionEditor
        section={section}
        lines={sectionLines}
        accentColor={accentColor}
        isDraft={isDraft}
        onRenameSection={onRenameSection}
        onRemoveSection={onRemoveSection}
        dragHandle={dragHandle}
      >
        {children}
      </SectionEditor>
    </div>
  );
}

function SortableLineItem({ id, section, line, idx, isDraft, onUpdateLine, onRemoveLine, onAddToCatalog }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } =
    useSortable({ id, data: { type: "line", section, idx } });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <LineRow
        line={line}
        idx={idx}
        isDraft={isDraft}
        onUpdate={(patch) => onUpdateLine(idx, patch)}
        onRemove={() => onRemoveLine(idx)}
        onAddToCatalog={onAddToCatalog}
        dragHandleProps={isDraft ? { ref: setActivatorNodeRef, ...attributes, ...listeners } : null}
        dragging={isDragging}
      />
    </div>
  );
}

// -------- Composant principal ---------------------------------------------
export default function SectionsList({
  quote,
  setQuote,
  sectionsOrder,
  linesBySection,
  catalog,
  accentColor,
  isDraft,
  onAddSection,
  onAddLine,
  onUpdateLine,
  onRemoveLine,
  onRenameSection,
  onRemoveSection,
  onReorderSections,
  onReorderLines,
  onMoveLineToSection,
  onAddFromCatalog,
  onAddLinesFromCatalog,
  onAddToCatalog,
}) {
  // Phase 2A — section active pour la modal catalogue multi-select
  const [catalogModalSection, setCatalogModalSection] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const aData = active.data.current;
    const oData = over.data.current;
    if (!aData || !oData) return;

    // Reorder sections
    if (aData.type === "section" && oData.type === "section") {
      const oldIdx = sectionsOrder.indexOf(aData.section);
      const newIdx = sectionsOrder.indexOf(oData.section);
      if (oldIdx >= 0 && newIdx >= 0) onReorderSections(arrayMove(sectionsOrder, oldIdx, newIdx));
      return;
    }

    // Line dragged
    if (aData.type === "line") {
      // Drop sur une section (bandeau) → déplace en fin de section cible
      if (oData.type === "section") {
        if (aData.section !== oData.section) {
          onMoveLineToSection(aData.idx, oData.section, "end");
        }
        return;
      }
      // Drop sur une autre ligne
      if (oData.type === "line") {
        if (aData.section === oData.section) {
          // reorder intra-section : indices relatifs à cette section
          const items = linesBySection[aData.section] || [];
          const sourceIdxInSec = items.findIndex((it) => it.idx === aData.idx);
          const targetIdxInSec = items.findIndex((it) => it.idx === oData.idx);
          if (sourceIdxInSec >= 0 && targetIdxInSec >= 0 && sourceIdxInSec !== targetIdxInSec) {
            onReorderLines(aData.section, sourceIdxInSec, targetIdxInSec);
          }
        } else {
          // Déplacement inter-section : insère à la position de la ligne cible
          onMoveLineToSection(aData.idx, oData.section, oData.idx);
        }
      }
    }
  };

  if (sectionsOrder.length === 0) {
    return (
      <div
        className="rounded border border-slate-800 bg-slate-950/30 p-6 text-center text-sm text-slate-500"
        data-testid="empty-sections"
      >
        Aucune section. Ajoutez-en une pour commencer à rédiger votre devis.
        {isDraft && (
          <div className="mt-3">
            <Button
              size="sm"
              onClick={() => onAddSection("Divers")}
              className="text-white"
              style={{ backgroundColor: accentColor }}
              data-testid="empty-sections-add-divers"
            >
              Démarrer avec « Divers »
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <SortableContext
        items={sectionsOrder.map((s) => `section:${s}`)}
        strategy={verticalListSortingStrategy}
      >
        {sectionsOrder.map((section, sIdx) => {
          const secLines = quote.lines.filter((l) => (l.section || "Divers") === section);
          const lineItems = linesBySection[section] || [];
          const lineIds = lineItems.map(({ line, idx }) => `line:${line.id || idx}`);
          return (
            <SortableSectionBlock
              key={section}
              section={section}
              sectionLines={secLines}
              sIdx={sIdx}
              sectionsOrder={sectionsOrder}
              accentColor={accentColor}
              isDraft={isDraft}
              onRenameSection={onRenameSection}
              onRemoveSection={onRemoveSection}
            >
              <SortableContext items={lineIds} strategy={verticalListSortingStrategy}>
                {lineItems.length > 0 ? (
                  lineItems.map(({ line, idx }) => (
                    <SortableLineItem
                      key={line.id || idx}
                      id={`line:${line.id || idx}`}
                      section={section}
                      line={line}
                      idx={idx}
                      isDraft={isDraft}
                      onUpdateLine={onUpdateLine}
                      onRemoveLine={onRemoveLine}
                      onAddToCatalog={onAddToCatalog}
                    />
                  ))
                ) : (
                  <p className="py-2 text-center text-xs text-slate-500">
                    Section vide. Glissez une ligne ici ou ajoutez-en une.
                  </p>
                )}
              </SortableContext>

              {isDraft && (
                <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 pt-2">
                  <CatalogPicker
                    items={catalog.filter((c) => !c.section || c.section === section)}
                    onPick={(item) => onAddFromCatalog(section, item)}
                    defaultSection={section}
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-slate-700 bg-slate-800/60 text-slate-200"
                        data-testid={`add-line-menu-${section}`}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter une ligne
                        <ChevronDown className="ml-1 h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="border-slate-700 bg-slate-900">
                      <DropdownMenuItem
                        onClick={() => onAddLine(section)}
                        data-testid={`add-line-standard-${section}`}
                      >
                        <Plus className="mr-2 h-3.5 w-3.5" /> Ligne standard
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setCatalogModalSection(section)}
                        data-testid={`add-line-catalog-${section}`}
                      >
                        <BookOpen className="mr-2 h-3.5 w-3.5" /> Depuis le catalogue…
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onAddLine(section, { is_note: true, description: "" })}
                        data-testid={`add-line-note-${section}`}
                      >
                        <StickyNote className="mr-2 h-3.5 w-3.5" /> Ligne de note
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onAddLine(section, { is_option: true })}
                        data-testid={`add-line-option-${section}`}
                      >
                        <Package className="mr-2 h-3.5 w-3.5" /> Ligne optionnelle
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </SortableSectionBlock>
          );
        })}
      </SortableContext>

      {isDraft && (
        <div className="mt-2 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddSection()}
            className="border-slate-700 bg-slate-800/60 text-slate-200"
            data-testid="add-section-btn-footer"
          >
            <FolderPlus className="mr-1.5 h-3.5 w-3.5" /> Ajouter une section
          </Button>
        </div>
      )}

      {/* Phase 2A — Modal multi-select catalogue (partagée pour toutes les sections) */}
      <CatalogMultiPickerModal
        open={!!catalogModalSection}
        onClose={() => setCatalogModalSection(null)}
        items={catalog}
        defaultSection={catalogModalSection}
        onConfirm={(selected) => {
          // Phase 2A fix FAIL #1 (2026-09-02) — Utilise l'insertion batch atomique.
          // Éviter `forEach(onAddFromCatalog)` : chaque appel synchrone lirait le
          // même snapshot `quote.lines` via closure et le dernier écraserait les
          // précédents (résultat : N-1 lignes insérées au lieu de N).
          onAddLinesFromCatalog(catalogModalSection, selected);
        }}
      />
    </DndContext>
  );
}
